import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { LoaderCircle, Sparkles } from 'lucide-react'
import * as React from 'react'

export const aiButtonSurfaceClass = `border-transparent text-white hover:text-white
   bg-gradient-to-r from-red-500/80 to-rose-500
   shadow-md hover:from-red-600 hover:to-rose-500
   transition-colors duration-150`

const aiButtonVariants = cva(
  `${aiButtonSurfaceClass}
   [&_svg]:pointer-events-none [&_svg]:shrink-0`,
  {
    variants: {
      size: {
        sm: "h-6 gap-1 px-2.5 text-xs font-light [&_svg:not([class*='size-'])]:size-3",
        md: "h-9 gap-1.5 px-3 text-xs font-light [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-2 px-4 text-sm font-medium [&_svg:not([class*='size-'])]:size-4",
        icon: "size-8 p-0 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

export type AIButtonSize = NonNullable<VariantProps<typeof aiButtonVariants>['size']>

export type AIButtonProps = Omit<React.ComponentProps<'button'>, 'children'> & {
  size?: AIButtonSize
  label?: React.ReactNode
  loading?: boolean
  loadingLabel?: React.ReactNode
  tooltipContent?: string
  disabledTooltipContent?: string
  children?: React.ReactNode
  tooltipContentClassName?: string
  icon?: React.ReactNode
}

export function AIButton({
  size = 'md',
  label,
  loading = false,
  loadingLabel = 'Generating...',
  tooltipContent,
  disabledTooltipContent,
  disabled,
  className,
  children,
  type = 'button',
  tooltipContentClassName,
  icon = <Sparkles />,
  ...props
}: AIButtonProps) {
  const isDisabled = Boolean(disabled || loading)
  const text = loading ? loadingLabel : (children ?? label)
  const showLabel = size !== 'icon' && Boolean(text)

  const button = (
    <Button
      type={type}
      variant='outline'
      size={size === 'icon' ? 'icon' : size === 'lg' ? 'default' : 'sm'}
      disabled={isDisabled}
      className={cn(aiButtonVariants({ size }), className)}
      {...props}
    >
      {loading ? <LoaderCircle className='animate-spin' /> : icon}
      {showLabel ? text : null}
    </Button>
  )

  const tooltip = isDisabled ? (disabledTooltipContent ?? tooltipContent) : tooltipContent
  if (!tooltip) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='inline-flex'>{button}</span>
      </TooltipTrigger>
      <TooltipContent className={cn(tooltipContentClassName)}>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  )
}
